import React, { useEffect, useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { MenuIcon, PhoneIcon, ShoppingCartIcon, UserIcon, XIcon } from 'lucide-react';
import { Logo } from './Logo';
import { SearchBar } from './SearchBar';
import { useCart } from '../../contexts/CartContext';
import { navLinks } from '../../data/navigation';
export function Header() {
  const {
    count,
    openCart
  } = useCart();
  const [menuOpen, setMenuOpen] = useState(false);
  const {
    pathname
  } = useLocation();
  useEffect(() => setMenuOpen(false), [pathname]);
  const navClass = ({
    isActive
  }: {
    isActive: boolean;
  }) => `relative flex h-12 items-center whitespace-nowrap transition-colors duration-150 ${isActive ? 'text-ink after:absolute after:inset-x-0 after:bottom-0 after:h-0.5 after:bg-ink' : 'text-ink/80 hover:text-ink'}`;
  return <header className="sticky top-0 z-40 border-b border-line bg-white" data-id="element-214" data-mp="3ufl72jx0lfyr">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8" data-id="element-215" data-mp="6w6u9mjf4xqi">
        <div className="flex h-16 items-center gap-3 lg:h-[76px] lg:gap-10" data-id="element-216" data-mp="6tkj7cxbk03j">
          <button type="button" onClick={() => setMenuOpen(o => !o)} className="-ml-2 flex h-10 w-10 items-center justify-center rounded-lg text-ink hover:bg-surface lg:hidden" aria-label={menuOpen ? 'Close menu' : 'Open menu'} aria-expanded={menuOpen} data-id="element-217" data-mp="bt6w1l98jebt">
            {menuOpen ? <XIcon className="h-5 w-5" data-id="element-218" data-mp="3p19y73xnf3pc" /> : <MenuIcon className="h-5 w-5" data-id="element-219" data-mp="3p2cr1hej7g7w" />}
          </button>

          <Logo data-id="element-220" data-mp="3u0eai3fvdhjw" />

          <div className="hidden max-w-2xl flex-1 md:block" data-id="element-221" data-mp="3tzv2beg0u7j2">
            <SearchBar data-id="element-222" data-mp="3u2h756bj28xl" />
          </div>

          <div className="ml-auto flex items-center gap-1" data-id="element-223" data-mp="1wu7f1sjrqnx">
            <a href="tel:+31851234567" className="mr-3 hidden items-center gap-2.5 xl:flex" data-id="element-224" data-mp="1u92u50v6wmh">
              <PhoneIcon className="h-4 w-4 text-muted" aria-hidden="true" data-id="element-225" data-mp="19c8g7t7uusys" />
              <span className="leading-tight" data-id="element-226" data-mp="1da63gqi2f4zj">
                <span className="block text-xs text-muted" data-id="element-227" data-mp="3ac4y3k4b4sl1">Expert advice</span>
                <span className="block whitespace-nowrap text-sm font-semibold text-ink" data-id="element-228" data-mp="3ae7y8wz6c23h">085 123 4567</span>
              </span>
            </a>
            <Link to="/" className="flex h-10 items-center gap-2 rounded-lg px-2.5 text-sm font-medium text-ink hover:bg-surface" data-id="element-229" data-mp="3jdbdfpv7zxcp">
              <UserIcon className="h-5 w-5" aria-hidden="true" data-id="element-230" data-mp="3eerrvqkjgnkv" />
              <span className="hidden sm:inline" data-id="element-231" data-mp="2qnmah6yy9n8g">Account</span>
            </Link>
            <button type="button" onClick={openCart} className="relative flex h-10 items-center gap-2 rounded-lg bg-ink px-3.5 text-sm font-semibold text-white hover:bg-ink-soft" aria-label={`Open cart, ${count} items`} data-id="element-232" data-mp="2mnc8s6i6xize">
              <ShoppingCartIcon className="h-[18px] w-[18px]" aria-hidden="true" data-id="element-233" data-mp="27xms12xm76e0" />
              <span className="hidden sm:inline" data-id="element-234" data-mp="27vjtfuiec4xo">Cart</span>
              <motion.span key={count} initial={{
              scale: 0.7
            }} animate={{
              scale: 1
            }} transition={{
              type: 'spring',
              stiffness: 600,
              damping: 20
            }} className="flex h-5 min-w-[20px] items-center justify-center rounded-full bg-volt px-1.5 text-[11px] font-bold text-ink" data-id="element-235" data-mp="22y1tuuq9r22c">
                {count}
              </motion.span>
            </button>
          </div>
        </div>

        <div className="pb-3 md:hidden" data-id="element-236" data-mp="1248hb8i38vdg">
          <SearchBar data-id="element-237" data-mp="1259r79ndbbtl" />
        </div>
      </div>

      <nav className="hidden border-t border-line lg:block" aria-label="Categories" data-id="element-238" data-mp="1663eq1wweoly">
        <ul className="mx-auto flex h-12 max-w-7xl items-center gap-8 px-8 text-sm font-medium" data-id="element-239" data-mp="s6klef8qxhtp">
          <li data-id="element-240" data-mp="x5ot11g0v7hx">
            <NavLink to="/shop" end className={navClass} data-id="element-241" data-mp="x5f47h6pr6dt">
              All products
            </NavLink>
          </li>
          {navLinks.map(link => <li key={link.label} data-id="element-242" data-mp="3cvi3vf8131v2">
              <NavLink to={link.href} className={navClass} data-id="element-243" data-mp="3hvmlj8koa2mv">
                {link.label}
              </NavLink>
            </li>)}
          <li className="ml-auto" data-id="element-244" data-mp="10ygmqjvbh8h">
            <NavLink to="/deals" className="flex h-12 items-center whitespace-nowrap font-semibold text-sale hover:underline" data-id="element-245" data-mp="11zuf2s9qhtw">
              Deals
            </NavLink>
          </li>
          <li data-id="element-246" data-mp="3odhct04g44bo">
            <Link to="/#advisor" className="flex h-12 items-center whitespace-nowrap text-muted hover:text-ink" data-id="element-247" data-mp="3oav1qqicj6op">
              Battery advisor
            </Link>
          </li>
        </ul>
      </nav>

      <AnimatePresence data-id="element-248" data-mp="jwojsh3jejny">
        {menuOpen && <motion.nav initial={{
        height: 0,
        opacity: 0
      }} animate={{
        height: 'auto',
        opacity: 1
      }} exit={{
        height: 0,
        opacity: 0
      }} transition={{
        duration: 0.22,
        ease: [0.23, 1, 0.32, 1]
      }} className="overflow-hidden border-t border-line lg:hidden" aria-label="Mobile categories" data-id="element-249" data-mp="fyh4lqoq1qev">
            <ul className="px-4 py-2 sm:px-6" data-id="element-250" data-mp="2qpqp7rv3pmtu">
              {[{
            label: 'All products',
            href: '/shop'
          }, ...navLinks, {
            label: 'Deals',
            href: '/deals'
          }].map(link => <li key={link.label} className="border-b border-line last:border-0" data-id="element-251" data-mp="2gjfqdppwunol">
                  <Link to={link.href} className={`block py-3 text-sm font-medium ${link.label === 'Deals' ? 'text-sale' : 'text-ink'}`} data-id="element-252" data-mp="2lkwlt5pmlhxs">
                    {link.label}
                  </Link>
                </li>)}
            </ul>
          </motion.nav>}
      </AnimatePresence>
    </header>;
}